import { discoverCategoryId } from "./discoverContent";
import { parseHomeView, type HomeView } from "../homeView";
import type {
  AppDetailsState,
  AppDetailsTab,
  DiscoverySelection,
  DiscoverySort,
  DiscoveryUrlState,
} from "./types";

const MAX_QUERY_LENGTH = 200;
const SAFE_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const SAFE_THEME_ID = /^[a-z][a-z0-9-]{0,63}$/;
const DISCOVERY_SORTS: readonly DiscoverySort[] = ["recommended", "for_you", "new", "rating", "popular"];

/** A known non-default order, else `undefined` (Recommended). */
export function parseDiscoverySort(value: string | null): DiscoverySort | undefined {
  return value && value !== "recommended" && (DISCOVERY_SORTS as readonly string[]).includes(value)
    ? (value as DiscoverySort)
    : undefined;
}

export function isDiscoveryHomeView(view: HomeView, enabled: boolean): boolean {
  return enabled && view !== "my-apps";
}

export function parseDiscoverySelection(
  value: string | null,
): DiscoverySelection | null {
  if (!value) return null;
  const separator = value.indexOf(":");
  if (separator < 0) return null;
  const kind = value.slice(0, separator);
  const id = value.slice(separator + 1);
  if (kind === "capsule") {
    const parts = id.split("/");
    return parts.length <= 2 &&
      parts.every((part) => SAFE_ID.test(part) && part !== "." && part !== "..")
      ? { kind, id }
      : null;
  }
  if (!SAFE_ID.test(id)) return null;
  if (kind === "instance") {
    return /^cinst_[A-Za-z0-9]+$/.test(id) ? { kind, id } : null;
  }
  if (kind === "discover" || kind === "preset") return { kind, id };
  return null;
}

/**
 * Preview is its own page: `/?c=<app>`. A Discover listing is its bare ID;
 * other apps keep their kind (`c=instance:cinst_…`, `c=capsule:alice/x`).
 * `?view=discover&app=<kind:id>` is the older form and still opens Preview.
 */
export function parsePreviewParam(
  value: string | null,
): DiscoverySelection | null {
  if (!value) return null;
  if (value.includes(":")) return parseDiscoverySelection(value);
  return SAFE_ID.test(value) ? { kind: "discover", id: value } : null;
}

export function previewParamValue(selection: DiscoverySelection): string {
  return selection.kind === "discover" && SAFE_ID.test(selection.id)
    ? selection.id
    : `${selection.kind}:${selection.id}`;
}

/** The Preview page's URL for one app. */
export function previewNavigationTarget(selection: DiscoverySelection): string {
  return `/?${new URLSearchParams({ c: previewParamValue(selection) }).toString()}`;
}

function singleParam(params: URLSearchParams, name: string): string | null {
  const values = params.getAll(name);
  return values.length === 1 ? values[0] : null;
}

function normalizeQuery(query: string): string {
  return query
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .trim()
    .slice(0, MAX_QUERY_LENGTH);
}

/** Syntax validation only; catalogue/library resolution verifies access later. */
export function parseDiscoveryUrl(
  pathname: string,
  search: string,
  enabled: boolean,
): DiscoveryUrlState | null {
  const params = new URLSearchParams(search);
  if (
    pathname !== "/" ||
    !isDiscoveryHomeView(
      parseHomeView(search),
      enabled || params.has("app") || params.has("c"),
    )
  ) {
    return null;
  }
  const themeId = singleParam(params, "theme");
  return {
    selection:
      parsePreviewParam(singleParam(params, "c")) ??
      parseDiscoverySelection(singleParam(params, "app")),
    // Links shared before genres keep working: retired ids map onto a genre.
    themeId:
      themeId && SAFE_THEME_ID.test(themeId)
        ? discoverCategoryId(themeId)
        : null,
    query: normalizeQuery(singleParam(params, "q") ?? ""),
    sort: parseDiscoverySort(singleParam(params, "sort")),
  };
}

/** Allowlist URL fields so runtime URLs, tokens and temporary IDs cannot leak. */
/** Grid (`/?view=discover`) without a selection; Preview (`/?c=`) with one.
 *  Category, query and a non-default sort ride along so the panel, the Grid,
 *  a reload and Back/Forward all show the same list. */
export function discoveryNavigationTarget(state: DiscoveryUrlState): string {
  const selection = state.selection
    ? parseDiscoverySelection(`${state.selection.kind}:${state.selection.id}`)
    : null;
  const params = selection
    ? new URLSearchParams({ c: previewParamValue(selection) })
    : new URLSearchParams({ view: "discover" });
  if (state.themeId && SAFE_THEME_ID.test(state.themeId)) {
    params.set("theme", state.themeId);
  }
  const query = normalizeQuery(state.query);
  if (query) params.set("q", query);
  const sort = parseDiscoverySort(state.sort ?? null);
  if (sort) params.set("sort", sort);
  return `/?${params.toString()}`;
}

/** Details are independent of the central selection and of the Home pane. */
export function parseAppDetailsUrl(
  pathname: string,
  search: string,
): AppDetailsState | null {
  if (pathname !== "/") return null;
  const params = new URLSearchParams(search);
  const target = parseDiscoverySelection(singleParam(params, "detail"));
  if (!target) return null;
  const raw = singleParam(params, "tab");
  const tab: AppDetailsTab =
    raw === "app" ||
    raw === "data" ||
    raw === "permissions" ||
    raw === "source" ||
    raw === "comments" ||
    (raw === "reviews" && target.kind !== "instance")
      ? raw
      : "overview";
  return { target, tab };
}

/** Canonical target for a legacy /apps/<instance> deep link.
 *
 * The detail is a modal on the home/Discover surface, not a page. Links that
 * predate that normalisation (or were copied from an older build) must land on
 * the same app rather than the 404 fallback.
 */
export function legacyInstanceDetailTarget(instanceId: string): string {
  return detailsNavigationTarget(
    { kind: "instance", id: instanceId },
    "overview",
    "/?view=my-apps",
  );
}

export function detailsNavigationTarget(
  target: DiscoverySelection,
  tab: AppDetailsTab = "overview",
  base = "/?view=discover",
): string {
  const url = new URL(base, "https://ato.run");
  url.pathname = "/";
  url.hash = "";
  if (url.searchParams.get("detail") !== `${target.kind}:${target.id}`) {
    url.searchParams.delete("qa_report");
    url.searchParams.delete("qa_attempt");
  }
  if (tab !== "reviews") url.searchParams.delete("qa_report");
  url.searchParams.set("detail", `${target.kind}:${target.id}`);
  if (tab === "overview") url.searchParams.delete("tab");
  else url.searchParams.set("tab", tab);
  return url.pathname + url.search;
}

/** Public introduction links contain only a public target, never runtime or viewer state. */
export function detailsShareTarget(target: DiscoverySelection): string | null {
  if (
    target.kind === "instance" ||
    !parseDiscoverySelection(`${target.kind}:${target.id}`)
  )
    return null;
  return detailsNavigationTarget(target);
}

export function capsuleTarget(
  slug: string,
  publisher?: string | null,
): DiscoverySelection {
  return { kind: "capsule", id: publisher ? `${publisher}/${slug}` : slug };
}

/** Compatibility only. Runtime recovery is a separate, authenticated entry point. */
export function migrateLegacyDetailsUrl(
  pathname: string,
  search: string,
): string | null {
  const params = new URLSearchParams(search);
  const open = /^\/apps\/(cinst_[A-Za-z0-9]+)\/open\/?$/.exec(pathname);
  if (open)
    return discoveryNavigationTarget({
      selection: { kind: "instance", id: open[1] },
      themeId: null,
      query: "",
    });
  const manage = /^\/apps\/(cinst_[A-Za-z0-9]+)\/?$/.exec(pathname);
  const instance = /^\/a\/(cinst_[A-Za-z0-9]+)\/?$/.exec(pathname);
  const listing = /^\/a\/(discover-[A-Za-z0-9._-]+)\/?$/.exec(pathname);
  let target: DiscoverySelection | null =
    manage || instance
      ? { kind: "instance", id: (manage || instance)![1] }
      : listing
        ? { kind: "discover", id: listing[1] }
        : null;
  if (!target && pathname.startsWith("/a/")) {
    try {
      const parts = pathname
        .slice(3)
        .replace(/\/$/, "")
        .split("/")
        .map(decodeURIComponent);
      const value =
        parts.length === 3 && parts[0] === "by"
          ? parts.slice(1).join("/")
          : parts.length === 1
            ? parts[0]
            : "";
      target = parseDiscoverySelection(`capsule:${value}`);
    } catch {
      return null;
    }
  }
  if (!target) return null;
  if (target.kind === "capsule" && params.get("autostart") === "1") {
    const recovery = new URLSearchParams({ target: target.id });
    for (const key of [
      "ref",
      "origin",
      "launch_payment",
      "launch_id",
      "session_id",
    ])
      if (params.has(key)) recovery.set(key, params.get(key)!);
    return `/launch/return?${recovery}`;
  }
  const tab = parseAppDetailsUrl(
    "/",
    `?detail=${encodeURIComponent(`${target.kind}:${target.id}`)}&tab=${params.get("tab") ?? "overview"}`,
  )!.tab;
  const base = new URLSearchParams({ view: "discover" });
  const startError = params.get("start_error");
  if (
    target.kind === "instance" &&
    startError &&
    /^[a-z][a-z0-9_]{0,63}$/.test(startError)
  )
    base.set("start_error", startError);
  return detailsNavigationTarget(target, tab, `/?${base}`);
}

export function capsuleDetailsTarget(
  slug: string,
  publisher?: string | null,
): string {
  return detailsNavigationTarget(capsuleTarget(slug, publisher));
}
export function instanceDetailsTarget(
  id: string,
  tab: AppDetailsTab = "overview",
): string {
  return detailsNavigationTarget({ kind: "instance", id }, tab);
}
export function launchReturnTarget(ref: string): string {
  return `/launch/return?${new URLSearchParams({ ref, origin: "billing_resume" })}`;
}
